import { type ReactNode, useEffect, useRef } from 'react';

interface Props {
  open: boolean;
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  className = '',
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onCancelOrClose = () => onClose();
    d.addEventListener('close', onCancelOrClose);
    // Light-dismiss fallback for browsers without closedBy (e.g. older Safari).
    let onClick: ((e: MouseEvent) => void) | undefined;
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      onClick = (e) => {
        if (e.target !== d) return;
        const r = d.getBoundingClientRect();
        const inside =
          r.top <= e.clientY &&
          e.clientY <= r.top + r.height &&
          r.left <= e.clientX &&
          e.clientX <= r.left + r.width;
        if (!inside) d.close();
      };
      d.addEventListener('click', onClick);
    }
    return () => {
      d.removeEventListener('close', onCancelOrClose);
      if (onClick) d.removeEventListener('click', onClick);
    };
  }, [onClose]);

  // Keep <dialog> mounted always; gate only the CONTENT on `open`. Unmounting on
  // close would skip the native close procedure and lose focus restoration (a11y).
  return (
    <dialog
      ref={ref}
      closedby="any"
      className={`bo-card p-0 w-[min(560px,92vw)] max-h-[88vh] backdrop:bg-black/45 backdrop:backdrop-blur-xs shadow-modal border-none rounded-xl ${className}`}
    >
      {open && (
        <>
          {title && (
            <div className="flex items-center justify-between px-5 py-4 border-b border-line-2 bg-surface-2/30">
              <h3 className="text-h3 font-extrabold text-ink">{title}</h3>
              <button
                type="button"
                aria-label="ปิด"
                className="text-ink-3 hover:text-ink hover:scale-110 active:scale-95 transition-all w-8 h-8 rounded-full flex items-center justify-center hover:bg-bg-warm"
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          )}
          <div className="p-5 overflow-y-auto">{children}</div>
        </>
      )}
    </dialog>
  );
}
